import React from "react";
import { Box, Typography } from "@mui/material";
import { C } from "../../../common/theme";
import { SalesOrder } from "../../../common/requests/sales/endpoints";
import { Banner, ConfirmDialog } from "../../../ui";
import { COMPLETABLE_STATUSES, formatMoney } from "../salesData";

interface CompleteOrderDialogProps {
  order: SalesOrder | null;
  busy: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (order: SalesOrder) => void;
}

/** Completing an order unlocks the sellers' earnings for withdrawal and
 * emails the buyer, so it's confirmed item by item before it runs. */
export default function CompleteOrderDialog({ order, busy, error, onCancel, onConfirm }: CompleteOrderDialogProps) {
  if (!order) return null;
  const toComplete = order.items.filter((i) => COMPLETABLE_STATUSES.includes(i.status));
  const skipped = order.items.filter((i) => !COMPLETABLE_STATUSES.includes(i.status) && i.status !== "Completed");

  return (
    <ConfirmDialog
      open
      title="Mark order as completed?"
      confirmLabel={`Complete ${toComplete.length} item${toComplete.length === 1 ? "" : "s"}`}
      busy={busy}
      error={error}
      onClose={onCancel}
      onConfirm={() => toComplete.length > 0 && onConfirm(order)}
      body={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body2">
            Order <b className="mono">{order.order_id.slice(0, 8)}</b>
            {order.buyer ? (
              <>
                {" "}
                for <b>{order.buyer}</b>
              </>
            ) : null}
            . This releases the sellers' earnings for withdrawal and sends the buyer the completion email. It can't be undone
            from here.
          </Typography>
          <Box>
            <Typography variant="overline" component="div" sx={{ color: C.muted }}>
              Will be completed ({toComplete.length})
            </Typography>
            <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2.5 }}>
              {toComplete.map((i) => (
                <li key={i.order_item_id}>
                  <Typography variant="body2">
                    {i.name ?? "Unnamed listing"} × {i.quantity} · {formatMoney(i.net, order.currency)}{" "}
                    <Box component="span" sx={{ color: C.muted, fontSize: 12 }}>
                      (now {i.status}
                      {i.seller ? `, seller ${i.seller}` : ""})
                    </Box>
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
          {skipped.length > 0 && (
            <Banner tone="warning">
              {skipped.length} item{skipped.length > 1 ? "s are" : " is"} left as is because {skipped.length > 1 ? "they are" : "it is"}{" "}
              {Array.from(new Set(skipped.map((i) => i.status))).join(" / ")}: {skipped.map((i) => i.name ?? "Unnamed listing").join(", ")}.
            </Banner>
          )}
        </Box>
      }
    />
  );
}
